import './departmentTree.css';
import type { Department } from './systemMasters';

/**
 * Department tree for grids and pickers (G5 parity): each parent followed by its children, with an expand /
 * collapse arrow, the tick box and name indented by depth. Shared by Division ▸ Departments (grid + Link
 * picker) and the Department detail's Child Department Link picker, so both draw the same tree.
 */

/** One department in tree order: how deep it sits and whether it has children in the same list. */
export interface TreeNode { depth: number; hasKids: boolean }
/** Rows in tree order plus each row's depth / has-children. */
export interface DeptTree { rows: Department[]; meta: Map<string, TreeNode> }
type Sel = Set<string | number>;

const byName = (a: Department, b: Department) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });

/**
 * Departments in G5 tree order: depth-first, siblings by name. A department whose parent is not in the list is a
 * root. Children of a collapsed department are left out.
 */
export function deptTree(list: Department[], collapsed: ReadonlySet<string>): DeptTree {
  const ids = new Set(list.map((d) => d.id));
  const kids = new Map<string, Department[]>();
  const roots: Department[] = [];
  for (const d of list) {
    if (d.parentId && ids.has(d.parentId) && d.parentId !== d.id) kids.set(d.parentId, [...(kids.get(d.parentId) ?? []), d]);
    else roots.push(d);
  }
  const rows: Department[] = [];
  const meta = new Map<string, TreeNode>();
  const seen = new Set<string>();
  const walk = (d: Department, depth: number) => {
    if (seen.has(d.id)) return; // guards a parent loop in the data
    seen.add(d.id);
    const children = (kids.get(d.id) ?? []).sort(byName);
    rows.push(d);
    meta.set(d.id, { depth, hasKids: children.length > 0 });
    if (!collapsed.has(d.id)) children.forEach((c) => walk(c, depth + 1));
  };
  roots.sort(byName).forEach((r) => walk(r, 0));
  return { rows, meta };
}

/** The tree, or — while a search is typed — the matching departments as a flat list by name. */
export function deptTreeOrMatches(list: Department[], collapsed: ReadonlySet<string>, search: string): DeptTree {
  const q = search.trim().toLowerCase();
  if (!q) return deptTree(list, collapsed);
  const rows = list.filter((d) => (d.name ?? '').toLowerCase().includes(q)).sort(byName);
  return { rows, meta: new Map<string, TreeNode>(rows.map((d) => [d.id, { depth: 0, hasKids: false }])) };
}

/** A toggle for a collapsed-set state: adds the id, or removes it when already there. */
export const toggleIn = (set: (f: (s: Set<string>) => Set<string>) => void) => (id: string) =>
  set((s0) => { const n = new Set(s0); if (n.has(id)) n.delete(id); else n.add(id); return n; });

/**
 * Department cell in G5 row order: expand / collapse arrow · tick box · name, the tick box and name indented by
 * depth so a child's box sits under its parent's name. Pair it with `className="dd-treegrid"` on the grid: the
 * grid's own tick column is hidden there but still owns the selection, so Space on a row keeps toggling it.
 */
export const treeCell = (meta: Map<string, TreeNode>, closed: ReadonlySet<string>, toggle: (id: string) => void,
  sel: Sel, setSel: (s: Sel) => void) => (r: Department) => {
  const m = meta.get(r.id) ?? { depth: 0, hasKids: false };
  return (
    <span className="dd-tree">
      {m.hasKids ? (
        <button type="button" className="dd-tree-tog" tabIndex={-1} aria-label={closed.has(r.id) ? 'Expand' : 'Collapse'}
          onClick={(e) => { e.stopPropagation(); toggle(r.id); }}>
          <i className={`ti ${closed.has(r.id) ? 'ti-chevron-right' : 'ti-chevron-down'}`} />
        </button>
      ) : <span className="dd-tree-sp" />}
      <span className="dd-tree-ind" style={{ width: m.depth * 18 }} />
      <input type="checkbox" className="dd-tree-cb" tabIndex={-1} aria-label={`Select ${r.name ?? ''}`}
        checked={sel.has(r.id)} onClick={(e) => e.stopPropagation()}
        onChange={() => { const n = new Set(sel); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); setSel(n); }} />
      {r.name ?? ''}
    </span>
  );
};

/** Column header with the select-all box, lined up over the row boxes. */
export const treeHeader = (label: string, ids: string[], sel: Sel, setSel: (s: Sel) => void) => {
  const all = ids.length > 0 && ids.every((id) => sel.has(id));
  return (
    <span className="dd-tree">
      <span className="dd-tree-sp" />
      <span className="dd-tree-ind" />
      <input type="checkbox" className="dd-tree-cb" tabIndex={-1} aria-label="Select all" checked={all}
        ref={(el) => { if (el) el.indeterminate = !all && ids.some((id) => sel.has(id)); }}
        onClick={(e) => e.stopPropagation()}
        onChange={() => setSel(all ? new Set() : new Set(ids))} />
      {label}
    </span>
  );
};
